// 月曆格的週期狀態 —— **純函數**（W58.1）。
//
// **為什麼這些不跟元件住在一起。** `CycleCellVisuals.tsx` 匯出元件，而 Vite
// 的 fast refresh 要求「一個檔案只匯出元件」—— 同一個檔案裡再匯出幾個
// 輔助函式，dev server 就會印三條
// `Fast refresh only works when a file only exports components`。
// 那三條是**我引入的**，而這一輪的要求是零警告，所以輔助函式搬到這裡。
//
// 這三個函式本身是純的、可單獨測的，而且**它們決定月曆格怎麼畫**：
//
// - `resolveCycleState` —— 把 cell 上的兩個欄位收斂成一個狀態；
// - `hasCycleVisual`   —— 這一天值得畫嗎；
// - `cycleVisualKind`   —— **這一天是哪一種視覺狀態**（底色 + 花 + 圖例
//   三件事都由它決定）；
// - `cycleWash`         —— 格底該染什麼色。

import type { CalendarDayCell, CycleCellState, CycleProjection } from "@/lib/api/calendar";

/**
 * 把 cell 上的兩個欄位收斂成一個狀態。
 *
 * **`inference` 的回退是刻意的。** `cycle_state` 是新的、資訊更全的欄位，
 * 但 `inference`（落在推斷經期帶上）還在契約裡，而且對經期來說兩者意思
 * 是一樣的。讀它當回退讓這個改動**嚴格是加的** —— 舊的 payload 仍然畫得
 * 出那條帶，位置與顏色都一樣。
 */
export function resolveCycleState(cell: CalendarDayCell): CycleCellState | null {
  if (cell.cycle_state) return cell.cycle_state;
  if (cell.inference) {
    return {
      phase: "menstrual",
      actual: false,
      predicted: true,
      range_start: false,
      range_end: false,
      ovulation_day: false,
      safe: false,
      flow: null,
    };
  }
  return null;
}

/** 這一天的週期狀態值得畫嗎？（連一個 state 都沒有 → 什麼都不畫） */
export function hasCycleVisual(state: CycleCellState | null): boolean {
  if (!state) return false;
  return Boolean(state.actual || state.predicted || state.phase || state.flow);
}

/**
 * 月曆格上**看得到的**五種狀態。
 *
 * **這是 W58.3 的重點：底色、花、圖例三件事由同一個函式決定。**
 *
 * W58.2 之前這三件事是**各自算的**：底色看 `cycleWash()`，花看
 * `markerKind()`，圖例寫死四項。三份「什麼算什麼狀態」的定義各自漂移，
 * 於是使用者看到圖例寫著「排卵」，而那一格其實是「排卵日」—— **圖例跟
 * 現實不齊**，這正是 W58.3 的反饋。
 *
 * 現在一份定義：`cycleVisualKind()`。它回傳的五個值**每一個都對應格子上
 * 真的會出現的樣子**：
 *
 * | 狀態 | 格底 | 花 | 圖例標籤 |
 * |---|---|---|---|
 * | `actual` | `bg-partner/25` | 實心玫瑰 | 經期 |
 * | `predicted` | `bg-partner/15` | 空心玫瑰 | 預測經期 |
 * | `ovulation-day` | `bg-shared/25` | 實心紫 | 排卵日 |
 * | `ovulation` | `bg-shared/25` | 空心紫 | 排卵期 |
 * | `fertile` | `bg-shared/15` | 無 | 易孕期 |
 *
 * **為什麼排卵日與排卵期是兩個值。** 它們的**底色一樣**（都是
 * `bg-shared/25`），分開它們的是**花的填色** —— 排卵日是實心紫，排卵期
 * 其餘的日子是空心紫。形狀通道，不是顏色通道。
 *
 * **回傳 `null` 的三種日子**：安全期（卵泡期 / 黃體期）、沒有被記錄也沒有
 * 被推算的經期、以及什麼都不知道的日子。它們在格子上**什麼都不畫** ——
 * 白底就是「安全期」這件事的樣子（Meetyou 也是這樣）。
 */
export type CycleVisualKind =
  | "actual"
  | "predicted"
  | "ovulation-day"
  | "ovulation"
  | "fertile";

export function cycleVisualKind(state: CycleCellState | null): CycleVisualKind | null {
  if (!state) return null;
  // **順序就是優先序**：實際記錄 > 推算 > 排卵日 > 排卵期 > 易孕期。
  // 「實際」永遠贏「推算」—— 事實贏猜測。
  if (state.actual) return "actual";
  if (state.predicted) return "predicted";
  if (state.ovulation_day) return "ovulation-day";
  if (state.phase === "ovulation") return "ovulation";
  if (state.phase === "fertile") return "fertile";
  return null;
}

/**
 * 格子底色 —— **狀態的主要視覺通道**（W58.2）。
 *
 * **為什麼底色是主要的。** 實測（2026-10-27，生產截圖）：使用者看到
 * 一排實心玫瑰花 + 一根從 6 號延伸到 10 號的粉線，反饋是
 * 「**現時下底橫線在用戶角度也不知是什麼東西**」。
 *
 * 原因不是線畫得不好，是**它孤零零地**。當時實際經期的底色是
 * `bg-partner-tint`（10% 玫瑰），在米白紙面上幾乎看不見 —— 於是那一格
 * 只有一根線，沒有任何東西說明它是什麼。
 *
 * 所以規則改成：**每一種週期狀態都有一個底色**，底色說「這幾天是什麼」，
 * 花說「是哪一種」。
 *
 * **W58.3 —— 線全部拿掉。** 底色升格成唯一的狀態通道之後，底色**下面**
 * 再壓一根線是冗餘的：預測經期同時有淡粉底 + 淡粉線、排卵期同時有淡紫
 * 底 + 淡紫線 —— 使用者看到的反饋正是「**都還有一條顏色線**」。
 *
 * | 狀態 | 底色 | 花的樣子 |
 * |---|---|---|
 * | 實際經期 | `bg-partner/25` | 實心玫瑰 |
 * | 預測經期 | `bg-partner/15` | 空心玫瑰 |
 * | 排卵日 | `bg-shared/25` | 實心紫 |
 * | 排卵期 | `bg-shared/25` | 空心紫 |
 * | 易孕期 | `bg-shared/15` | 無 |
 * | 安全期 | 無 | 無 |
 *
 * `bg-partner/25` 與 `bg-partner/15` 是 Tailwind opacity scale 上的值
 * （5 的倍數），所以兩條規則都會真的產生 CSS。`bg-partner-tint` 本身
 * 是 10% 玫瑰，**不再用** —— 那正是這次反饋的直接原因。
 */
export function cycleWash(state: CycleCellState | null): string {
  return cycleWashForKind(cycleVisualKind(state));
}

/**
 * 格底該染什麼色 —— **直接由視覺狀態決定**（W58.6）。
 *
 * **為什麼多這一個函式。** 圖例的色塊必須與月曆格的底色**同一個數字**，而圖例
 * 手上只有「狀態」沒有 `CycleCellState`。兩個函式各寫一份 switch 就是在給
 * 「同一個顏色」兩份真相 —— W58.3 記錄過那種漂移（「圖例寫『排卵』而格子其實
 * 是『排卵日』」）。所以圖例走這裡，**由同一份定義算出來**。
 */
export function cycleWashForKind(kind: CycleVisualKind | null): string {
  switch (kind) {
    case "actual":
      return "bg-partner/25";
    case "predicted":
      return "bg-partner/15";
    case "ovulation-day":
    case "ovulation":
      return "bg-shared/25";
    case "fertile":
      return "bg-shared/15";
    default:
      return "";
  }
}

/**
 * 降級推算的說明 —— **只在降級時存在**（W58.6 · ADR-0212）。
 *
 * **為什麼它在這裡而不在元件檔案。** Vite 的 fast refresh 要求「一個檔案只
 * 匯出元件」—— 純函式住在 `.ts` 裡，元件住在 `.tsx` 裡，這是本檔存在的理由。
 *
 * **為什麼只有降級才有說明。** `confidence_label` 後端**每次都給**
 * （`CONFIDENCE_ZH` 連 `high` 都有「相當穩定」）。把它寫在月曆首頁是拿一個
 * 稽核欄位當裝飾 —— 使用者看到的反饋正是「`· 黃體期 · 先用預設週期`」那種
 * 囉嗦。所以：
 *
 * - `preset` / `partial` → **降級推算**，必須說明它是怎麼來的（DATA_MODEL §5）；
 * - `low` / `medium` / `high` → 正常推算，不說。
 *
 * 週期頁的「推斷摘要」仍然列完整的心信心欄 —— 那是**看細節**的地方；月曆首頁
 * 是**第一眼**看到「經期 7 天後」的地方，所以降級時在這裡補一句。
 */
export function degradedBasis(projection: CycleProjection | null): string | null {
  const confidence = projection?.confidence;
  if (confidence !== "preset" && confidence !== "partial") return null;
  return projection?.confidence_label ?? null;
}
